import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LOGIN_ROUTE, MAINTENANCE_EVENT, MAINTENANCE_HEADER, REQUEST_ID_HEADER, deriveAsyncState, t, type AsyncState, SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT } from '@bms2/shared';
import { clearSessionToken, readSessionToken, sessionHeaders } from '../../session';
import type { RefreshedState } from '../../components/composites/AsyncStatePanel';
import { reportCaughtInBrowser, reportRefusalInBrowser } from '../../shell/systemErrors';

/**
 * The record pages' one read (piece 13). Every screen here fetches through this
 * hook, so the three fallback states of piece 4 — loading, empty and failed —
 * come from one place rather than being re-invented per page. 🔨 A screen that
 * cannot reach the API renders the failed panel with a retry, never a blank
 * region: a silent blank is the state the shell's fallbacks exist to refuse.
 */
/** 🔨 Relative to the API's own root: the base URL carries the prefix, this does not. */
export const PAGES_PATH = '/pages';

/**
 * 🔒 A picker's search names its own page, the largest the list route offers (decision 1890; T1276): the route answers every
 * matching row to a read that names none, and a picker shows its first few of these as it always did.
 */
export const PICKER_CAP = 'page=1&pageSize=200';
/** The deal routes, the file routes and the file types' route: one declaration each, imported by every screen that reads them (T2169). */
export const DEALS_PATH = '/deals';
export { FILES_PATH, fileThumbPath } from '../../components/files/filePaths';
/** 🔒 Decision 9740 (T3461): the people who can take a deleted person's open Deals, read through the delete's own gate. */
export const personSuccessorsPath = (personId: string): string => `${PAGES_PATH}/people/${encodeURIComponent(personId)}/successors`;
export const FILE_TYPES_PATH = '/file-types';
/** The one preference store's rows: a list page's saved views read and written here (piece 12). */
export const SAVED_VIEWS_PATH = '/shell/saved-views';
/** One saved view, written over by its version and deleted from Edit View (decision 2170). */
export const savedViewPath = (viewId: string): string => `${SAVED_VIEWS_PATH}/${encodeURIComponent(viewId)}`;
/** The Purchase Order's own routes; here rather than on a screen, because the page and its edit form both read them (decision 0409). */
export const PURCHASE_ORDERS_PATH = '/purchase-orders';
/** The sales-side documents' own routes, read by each document's page and by its edit form alike (decision 0409). */
export const SALES_ORDERS_PATH = '/sales-orders';
export const CHANGE_ORDERS_PATH = '/change-orders';
/** 🔒 How five documents' goods travel, the list their forms offer and Settings › Documents edits (decision 0682). */
export const SHIPMENT_PREFERENCES_PATH = '/shipment-preferences';
/** 🔒 The kinds of box a package is packed in, edited on Settings › Accounting › Package Types (decision 2530). */
export const PACKAGE_TYPES_PATH = '/package-types';
export const INVOICES_PATH = '/invoices';
export const CREDIT_NOTES_PATH = '/credit-notes';
export const PAYMENTS_PATH = '/payments';
/** The Bill's own routes (piece 24); the page and its edit form both read them, as the Purchase Order's do. */
export const BILLS_PATH = '/bills';
/** 🔒 The vendor's orders a Bill can be raised from (T140; decision 0711). */
export const openPurchaseOrdersPath = (vendorCompanyId: string): string => `${BILLS_PATH}/open-purchase-orders/${vendorCompanyId}`;
/** 🔒 What is left to bill on one order, which the Bill form fills its lines from (T140, T136; decision 0711). */
export const purchaseOrderRemainderPath = (purchaseOrderId: string): string => `${BILLS_PATH}/from-purchase-order/${purchaseOrderId}`;
/** 🔒 A fixed-asset line's Create Asset (decision 9700): a read for the window's prefill, a post for the save. */
export const billLineAssetPath = (billId: string, lineId: string): string => `${BILLS_PATH}/${encodeURIComponent(billId)}/lines/${encodeURIComponent(lineId)}/create-asset`;
/** The Vendor Credit's own routes (piece 22); its list raises one through the create route here (decision 0449). */
export const VENDOR_CREDITS_PATH = '/vendor-credits';
/** A confirmed shipment's own routes; the packing slip is the one a Purchase Order offers per shipment (piece 27). */
export const SHIPMENTS_PATH = '/shipments';
/** 🔒 The Package's routes (decisions 1730, 1731): its window, its page and slip, the order's packages, and the shipment raised from them. */
export const PACKAGES_PATH = '/packages';
export const PACKAGE_SHIP_FORM_PATH = `${PACKAGES_PATH}/ship-form`;
export const PACKAGE_SHIPMENTS_PATH = `${PACKAGES_PATH}/shipments`;
export const packageFormPath = (salesOrderId: string, packageId: string | null = null): string =>
  `${PACKAGES_PATH}/form?salesOrderId=${encodeURIComponent(salesOrderId)}${packageId ? `&packageId=${encodeURIComponent(packageId)}` : ''}`;
export const packagePath = (packageId: string): string => `${PACKAGES_PATH}/${packageId}`;
export const packageSlipPath = (packageId: string): string => `${PACKAGES_PATH}/${packageId}/packing-slip.pdf`;
export const orderPackagesPath = (salesOrderId: string): string => `${PACKAGES_PATH}?salesOrderId=${encodeURIComponent(salesOrderId)}`;
/**
 * 🔒 The sites that hold our own stock (decision 0451): the Organization's own
 * open sites, and no customer's. Every stock picker reads this one route, so a
 * transfer, a count, a revaluation and an Item's stock row cannot offer
 * different places to put the same shelf.
 */
export const STOCK_SITES_PATH = '/pages/stock-sites';

/**
 * 🔒 The Item Table's TAX column reads its options here, one search for every
 * priced document (decision 0435). A category is seeded reference data that
 * points at nothing and every document points at it (decision 0086), so the
 * four forms share one picker rather than each growing its own route.
 */
export function taxCategorySearch(apiBaseUrl = ''): (query: string, signal: AbortSignal) => Promise<{ id: string; line1: string }[]> {
  return async (query: string, signal: AbortSignal) => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/tax-categories?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; name: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.name }));
  };
}

/**
 * 🔒 The Item Table's ACCOUNT column reads its options here, one picker for
 * every priced document (decisions 0097, 0435). The label is the number and
 * the name together, which is how the Chart of Accounts reads an account
 * everywhere else.
 */
export function postingAccountSearch(apiBaseUrl = ''): (query: string, signal: AbortSignal) => Promise<{ id: string; line1: string }[]> {
  return async (query: string, signal: AbortSignal) => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/posting-accounts?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; accountNumber: string; name: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: `${row.accountNumber} · ${row.name}` }));
  };
}

/** The one label an account reads as on a line, so the server and the picker never spell it two ways. */
export function accountLabel(account: { accountNumber: string; name: string }): string {
  return `${account.accountNumber} · ${account.name}`;
}

/**
 * 🔨 While no session exists the shell holds the emulated employment and sends
 * it with each request (decision 0188). The shell writes the headers here once,
 * and every read and write inside the frame carries them; a screen never adds
 * its own. 💰 Before this store the shell's own read was the only request that
 * carried the emulation, so a record page under View as still answered for the
 * developer (found while building, 2026-09-09).
 */
let requestHeaders: Readonly<Record<string, string>> = {};

export function setRequestHeaders(headers: Readonly<Record<string, string>>): void {
  requestHeaders = headers;
}

/**
 * 🔒 The request id of the last read the server did not answer with success
 * (piece 9; decision 0822). A browser fault reported after it carries it, so the
 * two rows of one fault, the server's and the browser's, are joined.
 */
let lastFailedRequestId: string | null = null;

export function lastFailedRequest(): string | null {
  return lastFailedRequestId;
}

/**
 * 🔒 T1510 (decision 2200): a read the server answered with a refusal or a fault. A refusal writes no row, and a
 * fault's row is the server's own, named by the reference the message carries, so the read hook reports neither.
 */
class AnsweredReadError extends Error {}

/** A failure's text with its row's reference beside it, where the server wrote one (decision 0822). */
export function withErrorReference(message: string, reference: unknown): string {
  return typeof reference === 'string' && reference !== '' ? `${message} · ${t('shell.error.reference')} ${reference}` : message;
}

/** A request the API refused for want of a live session; the token is dropped and the Login Screen is where the person lands. */
export const UNAUTHENTICATED_STATUS = 401;

/** 🔒 The status a recovery's maintenance answers every request but health with (decision 4062). */
export const MAINTENANCE_STATUS = 503;

/**
 * 🔒 Decision 4062: a 503 carrying the maintenance header, or a body that says
 * maintenance, tells the shell to draw the maintenance page for every route.
 * The body is read from a clone, because a cross-origin answer hides the header
 * and the caller still reads its own copy.
 */
function noticeMaintenance(response: Response): void {
  if (response.status !== MAINTENANCE_STATUS || typeof window === 'undefined') return;
  const announce = (): void => {
    window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
  };
  if (response.headers?.get?.(MAINTENANCE_HEADER)) return announce();
  if (typeof response.clone !== 'function') return;
  response.clone().json().then((body: unknown) => {
    if ((body as { maintenance?: unknown } | null)?.maintenance === true) announce();
  }).catch(/* silent: a 503 whose body is not JSON is an ordinary outage, which the caller's own read reports. */ () => undefined);
}

/**
 * The one fetch every screen inside the frame uses, so the frame's headers
 * ride on all of them. 🔨 The session's bearer token rides with every request
 * too (piece 14), and a 401 on a request that carried one drops the token and
 * sends the person to the Login Screen: an expired or revoked session is
 * refused on its next request, not discovered a screen later.
 *
 * 🔒 T385 (P9-2): `skipAuthRedirect` opts a caller out of that send (decision
 * 1165). The share landing page is the one screen built for a reader who has
 * no login yet, and the frame's own redirect used to carry that reader
 * straight to the Login Screen before the page's own refusal panel could
 * render — the way out decision 0365 gives them was unreachable.
 */
export function apiFetch(url: string, init: RequestInit = {}, options: { skipAuthRedirect?: boolean } = {}): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  const session = sessionHeaders();
  for (const [name, value] of Object.entries({ ...session, ...requestHeaders })) headers.set(name, value);
  // 🔨 Any write sent from here may change a list the cache holds, so it drops them all (T648).
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') clearReadCache();
  return fetch(url, { ...init, headers }).then((response) => {
    if (method !== 'GET' && method !== 'HEAD') clearReadCache();
    // A test double answers without headers; the browser's own Response always carries them.
    if (!response.ok) lastFailedRequestId = response.headers?.get?.(REQUEST_ID_HEADER) ?? lastFailedRequestId;
    if (response.status === UNAUTHENTICATED_STATUS) clearReadCache();
    noticeMaintenance(response);
    if (response.status === UNAUTHENTICATED_STATUS && typeof window !== 'undefined' && !options.skipAuthRedirect) {
      // 🔨 A session that expired is re-authenticated in place by the frame's banner, never by a redirect that loses the page (piece 21).
      // A request that never held one goes to the Login Screen and comes back to the page it asked for. On the Login Screen itself a refused token is only dropped.
      if (readSessionToken()) {
        clearSessionToken();
        if (window.location.pathname !== LOGIN_ROUTE) window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      } else if (window.location.pathname !== LOGIN_ROUTE) {
        const next = `${window.location.pathname}${window.location.search}`;
        window.location.assign(next && next !== '/' ? `${LOGIN_ROUTE}?next=${encodeURIComponent(next)}` : LOGIN_ROUTE);
      }
    }
    return response;
  });
}

/**
 * 🔒 One short-lived read cache for reference lists (T648). A list that rarely changes (the chart of accounts, the reps, the file
 * types, the approved units) was read afresh by every page that drew it. A read
 * that opts in with `{ cache: true }` is answered from here for thirty seconds,
 * two mounts asking at once share one request, and an answer older than that is
 * still drawn while it is read again.
 *
 * 🔨 Every write drops the whole cache, so a list a person has just edited is
 * never drawn stale by the same browser: the written event, any write sent
 * through `apiFetch` and a session ending each clear it. A change made by
 * somebody else reaches a page already open within thirty seconds, not on the
 * next click. A read is keyed by its request headers as well as its address,
 * which separates people only under the development bearer transport and View
 * as; under the cookie transport the headers name nobody, so a change of person
 * is kept apart by the sign-out POST and a 401 each emptying the cache.
 */
export const READ_CACHE_MS = 30_000;

interface CachedRead {
  data: { body: unknown; denied: boolean };
  at: number;
}

const readCache = new Map<string, CachedRead>();
const readsInFlight = new Map<string, Promise<unknown>>();
let readCacheEpoch = 0;

/** Drops every cached answer and forgets the reads still in flight, so none of them is stored. */
export function clearReadCache(): void {
  readCache.clear();
  readsInFlight.clear();
  readCacheEpoch += 1;
}

function readCacheKey(url: string): string {
  return `${JSON.stringify({ ...sessionHeaders(), ...requestHeaders })} ${url}`;
}

/** What the cache holds for a read: the answer and whether it is still inside the window. */
function peekRead(url: string, now: number = Date.now()): { data: CachedRead['data']; fresh: boolean } | null {
  const held = readCache.get(readCacheKey(url));
  return held ? { data: held.data, fresh: now - held.at < READ_CACHE_MS } : null;
}

/** One network read of a cached list: the answer shared by whoever asked while it was in flight, stored only when the cache was not cleared meanwhile. */
function readShared<T extends { body: unknown; denied: boolean }>(url: string, fetchOnce: () => Promise<T>): Promise<T> {
  const key = readCacheKey(url);
  const running = readsInFlight.get(key);
  if (running) return running as Promise<T>;
  const epoch = readCacheEpoch;
  const started = fetchOnce().then(
    (data) => {
      if (epoch === readCacheEpoch) {
        // A refusal is never kept: the next mount asks again.
        if (!data.denied) readCache.set(key, { data, at: Date.now() });
        readsInFlight.delete(key);
      }
      return data;
    },
    (error: unknown) => {
      if (epoch === readCacheEpoch) readsInFlight.delete(key);
      throw error;
    },
  );
  readsInFlight.set(key, started);
  return started;
}

export interface Fetched<T> {
  state: AsyncState<T>;
  reload: () => void;
  /** The server refused the read for this identity: the page renders Access Denied, not the failed panel. */
  denied: boolean;
  /** 🔒 The server's own words for the refusal, which Access Denied draws where they are the document read's (decision 1632). */
  deniedReason?: string;
}

/**
 * 🔒 Whether the viewer may write, off the read's own answer (T2100). False
 * until a ready answer says true: while the read loads, after it fails or is
 * refused, and when the answer carries no such field, so a posting button is
 * never drawn on a guess and taken back.
 */
export function writeGate<T extends object>(state: AsyncState<T>, field: { [K in keyof T]-?: boolean extends Exclude<T[K], undefined> ? K : never }[keyof T]): boolean {
  return state.kind === 'ready' && (state.data as Record<PropertyKey, unknown>)[field] === true;
}

/** A refused read carries this status, which the page reads as Access Denied rather than as a failure. */
export const DENIED_STATUS = 403;

/** One answer as the hook holds it: the key it answered, the path and base it was for, and the last ready answer for that path. */
interface Answered<T> {
  key: string;
  target: string;
  state: AsyncState<T>;
  denied: boolean;
  last: { data: T } | null;
}

export interface ApiReadOptions {
  /** A reference list read through the short-lived cache above (T648): answered from it for thirty seconds, read again after, and again on `reload`. */
  cache?: boolean;
}

export function useApi<T>(path: string | null, apiBaseUrl = '', options: ApiReadOptions = {}): Fetched<T> {
  const cached = options.cache === true;
  const [attempt, setAttempt] = useState(0);
  const [answered, setAnswered] = useState<Answered<T> | null>(null);
  // A reload of a cached read goes to the server whatever the cache holds.
  const forced = useRef(false);
  const reload = useCallback(() => {
    forced.current = true;
    setAttempt((n) => n + 1);
  }, []);
  // A read refused for want of a session asks again once the banner has signed the person back in (piece 21).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(SESSION_RENEWED_EVENT, reload);
    return () => window.removeEventListener(SESSION_RENEWED_EVENT, reload);
  }, [reload]);
  const target = `${apiBaseUrl} ${path ?? ''}`;
  const key = `${attempt} ${target}`;

  /**
   * Loading and Empty are derived, never written back by an effect: a request
   * whose answer has not arrived under this key is still loading, and a screen
   * with nothing to ask for is Empty. Only the answer itself is state.
   *
   * 🔒 A reload keeps what is drawn (decision 1431). While a reload of the same
   * path and base is in flight the last ready answer stays ready, marked
   * `refreshing`, and a reload that fails keeps it beside the error. Only a
   * changed path or base counts as loading. The kept answer is the same object,
   * so a form that seeds from it does not reseed from the stale copy.
   */
  const current = answered?.key === key ? answered : null;
  const kept = answered?.target === target ? answered.last : null;
  // 🔒 A cached read is drawn from the cache at once on a mount that has not been asked to reload (T648).
  const held = cached && path !== null && attempt === 0 ? peekRead(`${apiBaseUrl}${path}`) : null;
  const heldBody = held ? (held.data.body as T) : null;
  const heldFresh = held?.fresh ?? false;
  const state = useMemo((): AsyncState<T> => {
    if (path === null) return { kind: 'empty' };
    if (current) {
      if (current.state.kind === 'failed' && !current.denied && kept) return { kind: 'ready', data: kept.data, reloadError: current.state.error } as RefreshedState<T>;
      return current.state;
    }
    if (heldBody !== null) return heldFresh ? { kind: 'ready', data: heldBody } : ({ kind: 'ready', data: heldBody, refreshing: true } as RefreshedState<T>);
    return kept ? ({ kind: 'ready', data: kept.data, refreshing: true } as RefreshedState<T>) : { kind: 'loading' };
  }, [path, current, kept, heldBody, heldFresh]);

  useEffect(() => {
    if (path === null) return undefined;
    const controller = new AbortController();
    const settle = (state: AsyncState<T>, denied: boolean) =>
      setAnswered((held) => ({ key, target, state, denied, last: state.kind === 'ready' ? { data: state.data } : held?.target === target ? held.last : null }));
    const url = `${apiBaseUrl}${path}`;
    const reloading = forced.current;
    forced.current = false;
    const fresh = cached && !reloading ? peekRead(url) : null;
    if (fresh?.fresh) {
      // 🔨 Held in the hook's own state too, so a write that empties the cache later does not turn a drawn list back into Loading.
      settle({ kind: 'ready', data: fresh.data.body as T }, false);
      return undefined;
    }
    const fetchAnswer = (signal?: AbortSignal) =>
      apiFetch(url, signal ? { signal } : {}).then(async (response): Promise<{ body: T | null; denied: boolean; reason?: string }> => {
        if (response.ok) return { body: (await response.json()) as T, denied: false };
        // 🔒 Decision 11940: a refusal the page is handed to show posts its row, source Refusal, joined to the server's by the request id.
        const requestId = response.headers?.get?.(REQUEST_ID_HEADER) ?? null;
        if (response.status === DENIED_STATUS) {
          // 🔒 The refusal's own sentence rides with it, so a document page can say why (decision 1632).
          const refused = typeof response.json === 'function' ? ((await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: unknown } | null) : null;
          reportRefusalInBrowser(response.status, typeof refused?.message === 'string' ? refused.message : null, requestId);
          return { body: null, denied: true, ...(typeof refused?.message === 'string' ? { reason: refused.message } : {}) };
        }
        // 🔒 A fault's answer carries its row's reference, and the failed panel shows it (decision 0822); a refusal's carries its sentence.
        const failed = typeof response.json === 'function' ? ((await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: unknown; errorReference?: unknown } | null) : null;
        if (response.status < 500) reportRefusalInBrowser(response.status, typeof failed?.message === 'string' ? failed.message : null, requestId);
        throw new AnsweredReadError(withErrorReference(`the server answered ${response.status}`, response.status >= 500 ? failed?.errorReference : undefined));
      });
    // A cached read is shared and never aborted by one caller leaving; the others waiting on it still want the answer.
    (cached ? readShared(url, () => fetchAnswer()) : fetchAnswer(controller.signal))
      .then(({ body, denied, reason }) => {
        if (controller.signal.aborted) return;
        if (denied) settle({ kind: 'failed', error: reason ?? 'access denied' }, true);
        else settle({ kind: 'ready', data: body as T }, false);
      })
      .catch((error: Error) => {
        // 🔒 T1510 (decision 2200): a read that failed in the browser itself writes a row; one the server answered has its own.
        if (!(error instanceof AnsweredReadError)) reportCaughtInBrowser('records.read', error);
        if (!controller.signal.aborted) settle({ kind: 'failed', error: error.message }, false);
      });
    return () => controller.abort();
  }, [key, target, path, apiBaseUrl, cached]);

  const denied = current?.denied ?? false;
  return { state, reload, denied, ...(denied && current?.state.kind === 'failed' && current.state.error !== 'access denied' ? { deniedReason: current.state.error } : {}) };
}

/** A list read, where nothing found is Empty rather than an empty Ready. */
export function useRows<R>(path: string | null, apiBaseUrl = '', options: ApiReadOptions = {}): Fetched<readonly R[]> {
  const fetched = useApi<{ rows: R[] }>(path, apiBaseUrl, options);
  const source = fetched.state;
  // 🔒 The kept answer's marks ride through (decision 1431), and the same answer derives the same state, so a reread moves nothing it need not.
  const state = useMemo((): AsyncState<readonly R[]> => {
    if (source.kind !== 'ready') return source as AsyncState<readonly R[]>;
    const derived = deriveAsyncState(false, null, source.data.rows);
    const { refreshing, reloadError } = source as RefreshedState<{ rows: R[] }>;
    if (derived.kind !== 'ready' || (!refreshing && reloadError === undefined)) return derived;
    return { ...derived, ...(refreshing ? { refreshing } : {}), ...(reloadError !== undefined ? { reloadError } : {}) } as RefreshedState<readonly R[]>;
  }, [source]);
  return { state, reload: fetched.reload, denied: fetched.denied, ...(fetched.deniedReason !== undefined ? { deniedReason: fetched.deniedReason } : {}) };
}

/**
 * A write's outcome. A refusal carries the server's whole answer as `body` too,
 * for the few refusals that say more than a sentence: the costed change to a
 * bill of materials names the documents it owes beside its words (decision 1466).
 */
export type WriteOutcome<T> = { ok: true; data: T } | { ok: false; message: string; status?: number; body?: unknown };

/** A write, with the server's refusal surfaced rather than swallowed. */
export async function post<T>(path: string, body: unknown, apiBaseUrl = ''): Promise<WriteOutcome<T>> {
  return write<T>('POST', path, body, apiBaseUrl);
}

/** A one-off read outside a hook: a preview a save asks for before it runs, answered as a write outcome so a refusal reads the same way (decision 0169). */
export async function get<T>(path: string, apiBaseUrl = '', signal?: AbortSignal): Promise<WriteOutcome<T>> {
  return write<T>('GET', path, undefined, apiBaseUrl, signal);
}

/**
 * 🔒 Raised on the window after every write the server accepted, carrying the
 * path written (T281). A list kept open beside a record — the left pane of the
 * record view — read its rows when it opened, and a Send, an Approve or a save
 * on the record moved the status the list still drew. The list listens and
 * reads again; the record page needs nothing, it holds the answer already.
 */
export const RECORD_WRITTEN_EVENT = 'bms2:record-written';

// 🔒 A write announced anywhere, and a session ending or renewing, empties the read cache (T648).
if (typeof window !== 'undefined') {
  for (const name of [RECORD_WRITTEN_EVENT, SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT]) window.addEventListener(name, clearReadCache);
}

function announceWrite(path: string): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(RECORD_WRITTEN_EVENT, { detail: { path } }));
}

async function write<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', path: string, body: unknown, apiBaseUrl: string, signal?: AbortSignal): Promise<WriteOutcome<T>> {
  try {
    const response = await apiFetch(`${apiBaseUrl}${path}`, {
      method,
      ...(signal ? { signal } : {}),
      ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });
    const payload = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: string | string[]; errorReference?: unknown } | null;
    if (!response.ok) {
      const message = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
      // 🔒 Decision 11940: a refusal handed to the screen posts its row, source Refusal.
      reportRefusalInBrowser(response.status, message ?? null, response.headers?.get?.(REQUEST_ID_HEADER) ?? null);
      return { ok: false, message: withErrorReference(message ?? `the server refused the write (${response.status})`, payload?.errorReference), status: response.status, body: payload };
    }
    if (method !== 'GET') announceWrite(path);
    return { ok: true, data: payload as T };
  } catch (error) {
    // A request its caller aborted is the person's Stop, not a fault (decision 2422).
    if (signal?.aborted) return { ok: false, message: 'stopped' };
    // 🔒 T1510 (decision 2200): the report leaves out a request the server never answered, so only a fault of the page's own writes a row.
    reportCaughtInBrowser('records.write', error);
    return { ok: false, message: 'the server could not be reached' };
  }
}

/** The same write, with the verb a record's own save uses. */
export async function put<T>(path: string, body: unknown, apiBaseUrl = ''): Promise<WriteOutcome<T>> {
  return write<T>('PUT', path, body, apiBaseUrl);
}

/** A removal: a tag taken off a record, a flag cleared. */
/** A delete; `body` rides only where the route reads one, as the Person delete's successor does (decision 9740). */
export async function del<T>(path: string, apiBaseUrl = '', body?: unknown): Promise<WriteOutcome<T>> {
  return write<T>('DELETE', path, body, apiBaseUrl);
}

/**
 * A file upload: multipart, so the browser sets the boundary itself and the
 * frame's headers still ride along.
 */
/** 🔒 A window's Stop aborts the request through `signal` (decision 2422); an aborted send reports no fault, since nothing failed. */
/** 🔨 A refusal carries its status and the server's payload, as a write's does, so a caller reads the reason the server named (T671); `wrote` returns false for an answer that wrote nothing, so no write event fires. */
export async function upload<T>(path: string, form: FormData, apiBaseUrl = '', signal?: AbortSignal, wrote?: (data: T) => boolean): Promise<WriteOutcome<T>> {
  try {
    const response = await apiFetch(`${apiBaseUrl}${path}`, { method: 'POST', body: form, ...(signal ? { signal } : {}) });
    const payload = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: string | string[]; errorReference?: unknown } | null;
    if (!response.ok) {
      const message = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
      reportRefusalInBrowser(response.status, message ?? null, response.headers?.get?.(REQUEST_ID_HEADER) ?? null);
      return { ok: false, message: withErrorReference(message ?? `the server refused the upload (${response.status})`, payload?.errorReference), status: response.status, body: payload };
    }
    if (!wrote || wrote(payload as T)) announceWrite(path);
    return { ok: true, data: payload as T };
  } catch (error) {
    if (signal?.aborted) return { ok: false, message: 'stopped' };
    // 🔒 T1510 (decision 2200): as the write's, a request the server never answered posts nothing.
    reportCaughtInBrowser('records.upload', error);
    return { ok: false, message: 'the server could not be reached' };
  }
}

/** A partial edit, the verb the Settings screens use on a row they change one field of (piece 15). */
export async function patch<T>(path: string, body: unknown, apiBaseUrl = ''): Promise<WriteOutcome<T>> {
  return write<T>('PATCH', path, body, apiBaseUrl);
}
